import { useEffect, useRef } from 'react';

/**
 * Refetch-on-return primitive (Sebastian ruling 2026-10-22).
 *
 * Fires `onReturn` when the user returns to the tab — specifically when
 * `document.visibilityState` flips to `'visible'` or when the window
 * gains focus. Throttled at a floor of `throttleMs` so a browser that
 * emits both events in quick succession on return (common on macOS +
 * iOS) only triggers one refetch.
 *
 * NOT a polling primitive. There is no interval. If the tab stays
 * visible the hook does nothing after the initial mount — ongoing
 * background refresh (e.g. while a wait screen is active) is the
 * `usePolling` primitive's job, which this hook deliberately does not
 * duplicate.
 *
 * WHY BOTH VISIBILITYCHANGE AND FOCUS
 * ────────────────────────────────────
 * `visibilitychange` fires when the tab becomes active after being
 * backgrounded or when the browser window itself returns from the
 * background. `focus` fires when the window regains focus from another
 * app, from DevTools, or from a cross-origin popup/redirect returning
 * to the opener. Chrome fires both on tab switch; Safari sometimes
 * fires only `focus` on window switch; Firefox + mobile browsers lag
 * `focus` behind `visibilitychange`. Listening to both and throttling
 * is cheaper than reasoning about per-browser dispatch order.
 *
 * STRIPE-RETURN SCENARIO (the originating motivation)
 * ───────────────────────────────────────────────────
 * Customer opens `/book/:orderId/bestellung` in tab A. Clicks "Jetzt
 * bestellen", Stripe Checkout opens in tab B. Pays. The success URL
 * lands in tab B's `/erfolg` → polls → eventually navigates to
 * `/book/:orderId`. Customer switches back to tab A — `tab A` is still
 * on the old `checkout_pending` envelope. Without this hook, tab A
 * would show the stale state until the next hand-reload or until the
 * `usePolling` cadence catches up (checkout_pending is NOT in the
 * polling list — polling never fires there). With this hook,
 * returning to tab A fires one refetch and the Route switches to
 * `PostPaymentWaitScreen` on its own.
 *
 * SEMANTICS
 * ─────────
 * `onReturn` is captured in a ref so a caller who inlines
 * `onReturn: () => refetch()` does not tear the listeners down on
 * every render.
 *
 * `enabled` (default true) is a kill-switch for test harnesses or
 * routes that should NOT fire on return (none today — kept for
 * forward-compat).
 *
 * The throttle is a MIN-INTERVAL — the first call fires immediately,
 * then subsequent calls within `throttleMs` are dropped. Trailing-edge
 * flush is NOT implemented: on return the customer wants the newest
 * state right now, and a 5 s trail would also silently refetch when
 * the customer is already looking at a non-stale screen.
 */
export interface UseRefetchOnReturnOptions {
  /**
   * Callback fired when the user returns to the tab (visibility →
   * visible OR window focus) and the throttle floor has elapsed.
   * Return value ignored; errors are the caller's responsibility.
   */
  readonly onReturn: () => void | Promise<void>;
  /**
   * Minimum milliseconds between fires. The next return event inside
   * this window is dropped silently. Must be > 0; falsy values short-
   * circuit the hook (no listeners mounted).
   */
  readonly throttleMs: number;
  /** Default true. False tears listeners down without clearing state. */
  readonly enabled?: boolean;
}

export function useRefetchOnReturn(options: UseRefetchOnReturnOptions): void {
  const { onReturn, throttleMs, enabled = true } = options;

  // Latest onReturn in a ref so inline arrow callers don't retear.
  const onReturnRef = useRef(onReturn);
  useEffect(() => {
    onReturnRef.current = onReturn;
  }, [onReturn]);

  // Last-fire timestamp lives in a ref so a cross-render return event
  // reads the same clock. Initialised to 0 so the FIRST return event
  // after mount is NOT throttled.
  const lastFireAtRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (!Number.isFinite(throttleMs) || throttleMs <= 0) return;

    const maybeFire = (): void => {
      const now = Date.now();
      if (now - lastFireAtRef.current < throttleMs) return;
      lastFireAtRef.current = now;
      const result = onReturnRef.current();
      if (result && typeof result === 'object' && 'then' in result) {
        // Swallow — caller owns error handling.
        (result as Promise<void>).catch(() => undefined);
      }
    };

    const visibilityHandler = (): void => {
      if (document.visibilityState === 'visible') maybeFire();
    };
    const focusHandler = (): void => {
      maybeFire();
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', focusHandler);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', focusHandler);
      }
    };
  }, [enabled, throttleMs]);
}
